import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clipboardService } from '../services/api';
import ClipboardItem from './ClipboardItem';
import { Search, Filter, RefreshCw, Loader2, FileText, LogOut, Settings } from 'lucide-react';
import toast from 'react-hot-toast';

const PublicPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    content_type: '',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });

  const fetchPublicItems = async (page = 1, search = searchTerm, filterParams = filters) => {
    try {
      const params = {
        page,
        limit: pagination.items_per_page,
        content_type: filterParams.content_type,
      };
      
      if (search) {
        params.search = search;
      }

      const response = await clipboardService.getPublicItems(params);
      
      // Debug: Log the first item to see if title field exists
      if (response.items && response.items.length > 0) {
        console.log('Public item structure:', response.items[0]);
        console.log('Title field exists:', 'title' in response.items[0]);
        console.log('Title value:', response.items[0].title);
      }
      
      setItems(response.items || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching public items:', error);
      toast.error('Failed to load public items');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPublicItems();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPublicItems(1, searchTerm, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchPublicItems(1, searchTerm, newFilters);
  };

  const handlePageChange = (page) => {
    fetchPublicItems(page, searchTerm, filters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPublicItems(pagination.current_page, searchTerm, filters);
  };

  const handleUpdateItem = (itemId, updates) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  };

  const clearFilters = () => {
    const clearedFilters = {
      content_type: '',
    };
    setFilters(clearedFilters);
    setSearchTerm('');
    fetchPublicItems(1, '', clearedFilters);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading public items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <Link to="/" className="flex flex-col items-start hover:opacity-80 transition-opacity">
                <img src="/big-clipboard-text-logo.jpg" alt="Big Clipboard Logo" className="h-10 w-auto" />
                <span className="text-sm text-primary-700 font-medium mt-1">Copy Once. Use Anywhere.</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="btn-secondary flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                My Clipboard
              </Link>
              {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
                <Link to="/admin" className="bg-red-100 hover:bg-red-200 text-red-700 border-red-300 px-4 py-2 rounded-lg flex items-center transition-colors">
                  <Settings className="h-4 w-4 mr-2" />
                  Admin
                </Link>
              )}
              <button
                onClick={() => window.location.href = '/login'}
                className="btn-secondary flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Public Clipboard</h1>
          <p className="text-gray-600">Discover and share interesting content from the community</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search public items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </form>
            </div>

            {/* Filters */}
            <div className="flex gap-4">
              <select
                value={filters.content_type}
                onChange={(e) => handleFilterChange('content_type', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="text">Text</option>
                <option value="link">Link</option>
                <option value="image">Image</option>
                <option value="long_text">Long Text</option>
                <option value="html">HTML</option>
              </select>

              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-gray-600">
            Showing {items.length} of {pagination.total_items} items
          </p>
          {pagination.total_pages > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Page {pagination.current_page} of {pagination.total_pages}</span>
            </div>
          )}
        </div>

        {/* Items List */}
        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Filter className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <ClipboardItem
                  item={item}
                  isPublicPage={true}
                  onUpdate={handleUpdateItem}
                />
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 border rounded-lg ${
                    page === pagination.current_page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPage; 